import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Link,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';

import { forgetBatchData } from '../batch/batchUpdate';
import { CRM_SET_CONTACT_FLAG } from '../contactDetail/contactDetail.queries';
import { CountryField } from '../contactDetail/edit/CountryField';
import { contactPath } from '../crmPaths';
import { TagsSelect } from '../outreach/Pickers';
import { forgetContactData } from '../tagging/tagging';
import {
  consentInput,
  createInput,
  CRM_CREATE_CONTACT,
  describeAddError,
  EMPTY_PERSON,
  existingMatches,
  fieldOfRefusal,
  NewPerson,
  personProblems,
  PhoneType,
} from './addContact';
import { CRM_CONTACT_OWNER_OPTIONS } from './contacts.queries';

interface AddContactDialogProps {
  onClose: () => void;
  /** The person is in: open their page. `consentFailed` when their opt-in could not be recorded. */
  onAdded: (contactId: number, name: string, consentFailed: boolean) => void;
}

const SUPPORT = ['1, strong support', '2, support', '3, undecided', '4, opposed', '5, strongly opposed'];

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, display: 'grid', gap: 1.5, minWidth: 0 }}>
    <Typography component="legend" variant="overline" color="text.secondary" sx={{ lineHeight: 1.6, mb: 0.5, p: 0 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/**
 * Adds one person to the CRM by hand: a name, how to reach them, where they
 * are, and optionally their address, support, point person, tags, a note and
 * their consent. **A CRM contact only:** no app account is made, and none is
 * linked even when an app member has the same email or number.
 *
 * Someone already in the CRM with the same email or number stops the add and is
 * listed, to open instead; "Add anyway" makes a separate record. Consent is
 * given after the person is in, as a one-person batch update, with the note
 * saying how they agreed.
 */
export const AddContactDialog = ({ onClose, onAdded }: AddContactDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const client = useApolloClient();
  const [p, setP] = useState<NewPerson>(EMPTY_PERSON);
  const [tried, setTried] = useState(false);
  const [more, setMore] = useState(false);
  const [create, created] = useMutation<{ crmCreateContact: { id: number; fullName: string | null } }>(CRM_CREATE_CONTACT);
  const [consent, consenting] = useMutation(CRM_SET_CONTACT_FLAG);
  const owners = useQuery<{ owners: Array<{ value: string; label: string }> }>(CRM_CONTACT_OWNER_OPTIONS, { skip: !more });

  const set = (patch: Partial<NewPerson>) => setP((now) => ({ ...now, ...patch }));
  const problems = personProblems(p);
  const matches = existingMatches(created.error);
  const refusedField = matches ? undefined : fieldOfRefusal(created.error);
  const errorOf = (key: keyof NewPerson) => (tried ? problems[key] : undefined) ?? (refusedField === key ? describeAddError(created.error) : undefined);
  const busy = created.loading || consenting.loading;

  const submit = async (allowDuplicate = false) => {
    setTried(true);
    if (Object.keys(problems).length) {
      if (problems.address1 || problems.supportLevel) setMore(true);
      return;
    }
    let person: { id: number; fullName: string | null } | undefined;
    try {
      const { data } = await create({ variables: { input: createInput(p), allowDuplicate } });
      person = data?.crmCreateContact;
    } catch {
      return; // Shown from `created.error`.
    }
    if (!person) return;
    forgetContactData(client.cache, 'selection');

    let consentFailed = false;
    const change = consentInput(p);
    if (change) {
      try {
        await consent({ variables: { filter: { ids: [person.id] }, change }, update: (cache) => forgetBatchData(cache) });
      } catch {
        consentFailed = true;
      }
    }
    onAdded(person.id, person.fullName || [p.firstName, p.lastName].filter(Boolean).join(' '), consentFailed);
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-add-contact-title">
      <DialogTitle id="crm-add-contact-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: '10px', display: 'grid', placeItems: 'center', color: 'primary.main', bgcolor: alpha('#4f46e5', 0.1) }}>
          <PersonAddAlt1OutlinedIcon fontSize="small" />
        </Box>
        Add a person
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
          They are added to the CRM only. No app account is made for them: they can join the app themselves.
        </Typography>

        {matches && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => submit(true)} disabled={busy}>
                Add anyway
              </Button>
            }
          >
            <AlertTitle>Already in the CRM?</AlertTitle>
            {matches.length ? 'These contacts have the same ' : 'A contact has the same email or number.'}
            {matches.length > 0 && (
              <>
                email or number. Open one to update it, or add this person as a separate record.
                <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
                  {matches.map((m) => (
                    <li key={`${m.id}-${m.on}`}>
                      <Link component={RouterLink} to={contactPath(m.id)} target="_blank" rel="noopener" underline="hover">
                        {m.name || `Contact #${m.id}`}
                      </Link>{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        same {m.on === 'EMAIL' ? 'email' : 'number'}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </>
            )}
          </Alert>
        )}
        {created.error && !matches && !refusedField && <Alert severity="error">{describeAddError(created.error)}</Alert>}

        <Section title="Name">
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField label="First name" value={p.firstName} onChange={(e) => set({ firstName: e.target.value })} error={!!errorOf('firstName')} helperText={errorOf('firstName')} autoFocus inputProps={{ maxLength: 100 }} />
            <TextField label="Last name" value={p.lastName} onChange={(e) => set({ lastName: e.target.value })} inputProps={{ maxLength: 100 }} />
          </Box>
        </Section>

        <Section title="How to reach them">
          <TextField label="Email" type="email" value={p.email} onChange={(e) => set({ email: e.target.value })} error={!!errorOf('email')} helperText={errorOf('email')} inputProps={{ maxLength: 254 }} />
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 140px' } }}>
            <TextField
              label="Phone"
              value={p.phone}
              onChange={(e) => set({ phone: e.target.value })}
              error={!!errorOf('phone')}
              helperText={errorOf('phone') ?? '+233 24 412 3456, or national in their country'}
              inputProps={{ maxLength: 40, inputMode: 'tel' }}
            />
            <TextField select label="Type" value={p.phoneType} onChange={(e) => set({ phoneType: e.target.value as PhoneType })}>
              <MenuItem value="MOBILE">Mobile</MenuItem>
              <MenuItem value="HOME">Home</MenuItem>
              <MenuItem value="WORK">Work</MenuItem>
            </TextField>
          </Box>
        </Section>

        <Section title="Where">
          <CountryField value={p.countryCode} onChange={(countryCode) => set({ countryCode })} error={!!errorOf('countryCode')} helperText={errorOf('countryCode')} />
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField label="Region or state" value={p.region} onChange={(e) => set({ region: e.target.value })} inputProps={{ maxLength: 100 }} />
            <TextField label="Town or city" value={p.city} onChange={(e) => set({ city: e.target.value })} error={!!errorOf('city')} helperText={errorOf('city')} inputProps={{ maxLength: 100 }} />
          </Box>
        </Section>

        <Section title="Consent">
          <Box sx={{ display: 'grid', gap: 0.25 }}>
            <FormControlLabel control={<Checkbox checked={p.emailOptIn} onChange={(e) => set({ emailOptIn: e.target.checked })} />} label="They agreed to receive emails" />
            {errorOf('emailOptIn') && <FormHelperText error sx={{ mt: -0.5, ml: 4 }}>{errorOf('emailOptIn')}</FormHelperText>}
            <FormControlLabel control={<Checkbox checked={p.smsOptIn} onChange={(e) => set({ smsOptIn: e.target.checked })} />} label="They agreed to receive texts" />
            {errorOf('smsOptIn') && <FormHelperText error sx={{ mt: -0.5, ml: 4 }}>{errorOf('smsOptIn')}</FormHelperText>}
          </Box>
          <Collapse in={p.emailOptIn || p.smsOptIn} unmountOnExit>
            <TextField
              label="How did they agree?"
              placeholder="Signed the sheet at the Atlanta meet-up, 12 Oct"
              value={p.consentNote}
              onChange={(e) => set({ consentNote: e.target.value })}
              error={!!errorOf('consentNote')}
              helperText={errorOf('consentNote') ?? 'Kept with their consent and shown on their timeline.'}
              required
              fullWidth
              multiline
              minRows={2}
              inputProps={{ maxLength: 1000 }}
            />
          </Collapse>
          {!p.emailOptIn && !p.smsOptIn && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Unticked, they are not emailed or texted until they agree. Do this from their page later.
            </Typography>
          )}
        </Section>

        <Box>
          <Button size="small" onClick={() => setMore((m) => !m)} endIcon={<ExpandMoreIcon sx={{ transform: more ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}>
            {more ? 'Fewer details' : 'Address, support, tags and a note'}
          </Button>
          <Collapse in={more}>
            <Box sx={{ display: 'grid', gap: 2.5, pt: 2 }}>
              <Section title="Address">
                <TextField label="First line" value={p.address1} onChange={(e) => set({ address1: e.target.value })} error={!!errorOf('address1')} helperText={errorOf('address1') ?? 'With an address, its town and region become their place.'} inputProps={{ maxLength: 100 }} />
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 140px' } }}>
                  <TextField label="Second line" value={p.address2} onChange={(e) => set({ address2: e.target.value })} inputProps={{ maxLength: 100 }} />
                  <TextField label="Postcode" value={p.zip} onChange={(e) => set({ zip: e.target.value })} inputProps={{ maxLength: 20 }} />
                </Box>
              </Section>
              <Section title="Outreach">
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  <TextField select label="Support level" value={p.supportLevel ?? ''} onChange={(e) => set({ supportLevel: e.target.value === '' ? null : Number(e.target.value) })}>
                    <MenuItem value="">Not known</MenuItem>
                    {SUPPORT.map((label, i) => (
                      <MenuItem key={label} value={i + 1}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Stage" placeholder="Volunteer" value={p.stage} onChange={(e) => set({ stage: e.target.value })} error={!!errorOf('stage')} helperText={errorOf('stage')} inputProps={{ maxLength: 50 }} />
                </Box>
                <TextField
                  select
                  label="Point person"
                  value={p.pointPerson ?? ''}
                  onChange={(e) => set({ pointPerson: e.target.value === '' ? null : Number(e.target.value) })}
                  helperText={owners.error ? 'Could not load the point people.' : undefined}
                >
                  <MenuItem value="">Nobody yet</MenuItem>
                  {(owners.data?.owners ?? [])
                    .filter((o) => /^\d+$/.test(o.value))
                    .map((o) => (
                      <MenuItem key={o.value} value={Number(o.value)}>
                        {o.label}
                      </MenuItem>
                    ))}
                </TextField>
                {more && <TagsSelect label="Tags" value={p.tagIds} onChange={(tagIds) => set({ tagIds })} max={20} error={errorOf('tagIds')} />}
              </Section>
              <Section title="Note">
                <TextField label="A first note on their record" placeholder="Met at the Atlanta meet-up; wants to volunteer on weekends" value={p.note} onChange={(e) => set({ note: e.target.value })} multiline minRows={3} inputProps={{ maxLength: 5000 }} />
              </Section>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => submit(false)}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PersonAddAlt1OutlinedIcon />}
        >
          Add person
        </Button>
      </DialogActions>
    </Dialog>
  );
};
